/**
 * /agreement-users API — Named RM relationship CRUD (#276, 2026-06-08).
 *
 * Backend was shipped under #271. Today, Compliance and Admin auto-apply
 * writes (no CR queue); RM/read-only roles cannot reach the write
 * endpoints (403 at the ComplianceUser dependency). The hooks still
 * return WriteResult<AgreementUser> — same envelope as every other
 * entity that flows through maker-checker — so the Save aggregation in
 * AgreementDetailPage already handles the future case where
 * /agreement-users permissions widen to RM (Task 1, 2026-06-09).
 *
 * Server stamps `assigned_at` and `assigned_by_user_id` on create —
 * clients send only agreement_id, user_id, is_primary.
 */
import { api } from './client';
import type {
  AgreementUser,
  AgreementUserCreateInput,
  AgreementUserUpdateInput,
  Page,
  UUID,
  WriteResult,
} from './types';

export interface AgreementUsersListParams {
  agreement_id?: UUID;
  user_id?: UUID;
  is_primary?: boolean;
  include_deleted?: boolean;
  limit?: number;
  offset?: number;
}

// Firm-wide Named-RM feed (C1) — the Admin "RMs" card. One row per alive agreement
// with its RM set + labels/status. Mirrors backend schemas/agreement_user.py.
export interface NamedRmEntry {
  assignment_id: UUID;
  user_id: UUID;
  user_display_name: string;
  is_primary: boolean;
}
export interface FirmWideRmRow {
  agreement_id: UUID;
  agreement_code: string | null;
  agreement_mnemonic: string;
  agreement_status: string;
  rms: NamedRmEntry[];
}

function qs(p: object): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(p)) {
    if (v !== undefined && v !== null && v !== '') u.set(k, String(v));
  }
  return u.toString() ? `?${u}` : '';
}

// Writes go through maker-checker (#22-#26). Backend returns:
//   200/201 OK     — auto-applied (admin/compliance), live row mutated
//   202 Accepted   — filed PENDING (rm/staff), live row unchanged
// Plus headers X-Change-Request-Id and X-Change-Request-Status.
// Mirror of api/agreements.ts writeWithMaker — same envelope shape.
async function writeWithMaker<T>(
  call: () => Promise<{ data: T; status: number; headers: Headers }>,
): Promise<WriteResult<T>> {
  const r = await call();
  return {
    entity: r.data,
    pending: r.headers.get('X-Change-Request-Status') === 'pending',
    changeRequestId: r.headers.get('X-Change-Request-Id'),
  };
}

export const agreementUsersApi = {
  list:    (p: AgreementUsersListParams = {}) =>
    api.get<Page<AgreementUser>>(`/agreement-users${qs(p)}`),
  firmWide: () =>
    api.get<FirmWideRmRow[]>('/agreement-users/firm-wide'),
  get:     (id: UUID) =>
    api.get<AgreementUser>(`/agreement-users/${id}`),
  create:  (payload: AgreementUserCreateInput) =>
    writeWithMaker(() => api.postMeta<AgreementUser>('/agreement-users', payload)),
  update:  (id: UUID, payload: AgreementUserUpdateInput) =>
    writeWithMaker(() => api.patchMeta<AgreementUser>(`/agreement-users/${id}`, payload)),
  delete:  (id: UUID) =>
    writeWithMaker(() => api.deleteMeta<AgreementUser>(`/agreement-users/${id}`)),
  restore: (id: UUID) =>
    writeWithMaker(() => api.postMeta<AgreementUser>(`/agreement-users/${id}/restore`)),
};
